import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {Alert, Box, Button, CircularProgress, Stack, Typography} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {Question, QuestionStatus} from '@cp/model';
import PageHeader from '../../../components/PageHeader';
import QuestionEditForm from '../../../components/QuestionEditForm/QuestionEditForm';
import createEmptyQuestionData from '../../../components/QuestionEditForm/utils/createEmptyQuestionData';
import mapQuestionDataToQuestion from '../../../components/QuestionEditForm/utils/mapQuestionDataToQuestion';
import mapQuestionDataToQuestionCheckInput from '../../../components/QuestionEditForm/utils/mapQuestionDataToQuestionCheckInput';
import StatusNoteDialog from '../../../components/StatusNoteDialog/StatusNoteDialog';
import {PrivateRouteProps} from '../../../config/routeType';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import {useGenerateQuestionGraphJson} from '../../../hooks/generate/useGenerateQuestionGraphJson';
import {useCheckQuestionWithGptDirect} from '../../../hooks/question/useCheckQuestionWithGptDirect';
import {useCheckQuestionWithRulesDirect} from '../../../hooks/question/useCheckQuestionWithRulesDirect';
import {useCreateQuestion} from '../../../hooks/question/useCreateQuestion';
import {QuestionFormData} from '../../../types/QuestionFormData';
import AdminQuestionNewHeader from './AdminQuestionNewHeader';

export default function AdminQuestionNewPage(_props: PrivateRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {examModules, loading: modulesLoading} = useListExamModules();
  const {createQuestion, loading: createLoading} = useCreateQuestion();
  const {showMessage} = useMessage();
  const {generateQuestionGraphJson, loading: generateGraphLoading} = useGenerateQuestionGraphJson();
  const {checkQuestionWithRulesDirect, loading: checkRulesLoading} = useCheckQuestionWithRulesDirect();
  const {checkQuestionWithGptDirect, loading: checkGptLoading} = useCheckQuestionWithGptDirect();

  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [openGraphJsonPromptDialog, setOpenGraphJsonPromptDialog] = useState(false);
  const [openPasteQuestionDialog, setOpenPasteQuestionDialog] = useState(false);
  const [formData, setFormData] = useState<QuestionFormData>(() => {
    const locationState = location.state as {moduleId?: string; topic?: string; difficulty?: number} | undefined;
    const emptyData = createEmptyQuestionData(locationState?.moduleId);
    if (locationState) {
      return {
        ...emptyData,
        moduleId: locationState.moduleId || emptyData.moduleId,
        topic: locationState.topic || emptyData.topic,
        difficulty: locationState.difficulty ?? emptyData.difficulty,
      };
    }
    return emptyData;
  });

  // Reset form when location state changes (for "Create and Add Another")
  useEffect(() => {
    const locationState = location.state as {moduleId?: string; topic?: string; difficulty?: number} | undefined;
    if (locationState) {
      const emptyData = createEmptyQuestionData(locationState.moduleId);
      setFormData({
        ...emptyData,
        moduleId: locationState.moduleId || emptyData.moduleId,
        topic: locationState.topic || emptyData.topic,
        difficulty: locationState.difficulty ?? emptyData.difficulty,
      });
    }
  }, [location.state]);
  const [checkResultDialogOpen, setCheckResultDialogOpen] = useState(false);
  const [checkResult, setCheckResult] = useState<Question | null>(null);

  const handleBack = useCallback(() => {
    navigate('/admin/question');
  }, [navigate]);

  const handleGenerateClick = useCallback(() => {
    setOpenGenerateDialog(true);
  }, []);

  const handleApply = useCallback((newFormData: Partial<QuestionFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...newFormData,
      hint: newFormData.hint ?? prev.hint,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    const questionData = mapQuestionDataToQuestion(formData);
    if (!questionData) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    try {
      const newQuestion = await createQuestion(
        questionData.moduleId,
        questionData.topic,
        questionData.difficulty,
        questionData.questionText,
        questionData.options,
        questionData.answer,
        questionData.explanation,
        questionData.hint,
        questionData.image,
        questionData.graphJsonString || undefined,
      );

      if (newQuestion) {
        showMessage('Question created successfully', 6000, 'success');
        navigate('/admin/question');
      }
    } catch (err) {
      console.error('Error creating question:', err);
      showMessage('Failed to create question. Please try again.', 6000, 'error');
    }
  }, [formData, createQuestion, navigate, showMessage]);

  const handleSaveAndAddAnother = useCallback(async () => {
    const questionData = mapQuestionDataToQuestion(formData);
    if (!questionData) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    try {
      const newQuestion = await createQuestion(
        questionData.moduleId,
        questionData.topic,
        questionData.difficulty,
        questionData.questionText,
        questionData.options,
        questionData.answer,
        questionData.explanation,
        questionData.hint,
        questionData.image,
        questionData.graphJsonString || undefined,
      );

      if (newQuestion) {
        showMessage('Question created successfully', 6000, 'success');
        // Navigate to new question page with pre-set module, topic, and difficulty
        navigate('/admin/question/new', {
          state: {
            moduleId: questionData.moduleId,
            topic: questionData.topic,
            difficulty: questionData.difficulty,
          },
        });
      }
    } catch (err) {
      console.error('Error creating question:', err);
      showMessage('Failed to create question. Please try again.', 6000, 'error');
    }
  }, [formData, createQuestion, navigate, showMessage]);

  const handleFormDataChange = useCallback((data: QuestionFormData) => {
    setFormData(data);
  }, []);

  const selectedModule = examModules.find((module) => module.id === formData.moduleId);

  const handleCheckWithRules = useCallback(async () => {
    const questionCheckInput = mapQuestionDataToQuestionCheckInput(formData, examModules);
    if (!questionCheckInput) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    try {
      const result = await checkQuestionWithRulesDirect(questionCheckInput);

      if (result) {
        setCheckResult(result);
        setCheckResultDialogOpen(true);
      }
    } catch {
      showMessage('Failed to check question with rules', 6000, 'error');
    }
  }, [formData, examModules, checkQuestionWithRulesDirect, showMessage]);

  const handleCheckWithGpt = useCallback(async () => {
    const questionCheckInput = mapQuestionDataToQuestionCheckInput(formData, examModules);
    if (!questionCheckInput) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    if (!selectedModule) {
      showMessage('Please select a module', 6000, 'error');
      return;
    }

    try {
      const result = await checkQuestionWithGptDirect(questionCheckInput, selectedModule.name);

      if (result) {
        setCheckResult(result);
        setCheckResultDialogOpen(true);
      }
    } catch {
      showMessage('Failed to check question with GPT', 6000, 'error');
    }
  }, [formData, examModules, selectedModule, checkQuestionWithGptDirect, showMessage]);

  const handleGenerateGraphJson = useCallback(async () => {
    const questionCheckInput = mapQuestionDataToQuestionCheckInput(formData, examModules);
    if (!questionCheckInput) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    try {
      const topic = [
        questionCheckInput.moduleTopic.category,
        questionCheckInput.moduleTopic.topic,
        questionCheckInput.moduleTopic.detail,
      ]
        .filter(Boolean)
        .join(' > ');

      const graphJson = await generateQuestionGraphJson(
        formData.questionText,
        topic,
        formData.difficulty,
        formData.hint || undefined,
        formData.options,
        formData.answer,
        formData.explanation,
      );

      if (graphJson) {
        setFormData((prev) => ({...prev, graphJson}));
        showMessage('Graph JSON generated successfully', 6000, 'success');
      } else {
        showMessage('No graph needed for this question', 6000, 'info');
      }
    } catch {
      showMessage('Failed to generate graph JSON. Please try again.', 6000, 'error');
    }
  }, [formData, examModules, generateQuestionGraphJson, showMessage]);

  const handleCloseCheckResultDialog = useCallback(() => {
    setCheckResultDialogOpen(false);
    setCheckResult(null);
  }, []);

  return (
    <ContentContainer data-testid="question-new-page">
      <Stack spacing={3}>
        <PageHeader title="Create New Question" />

        <AdminQuestionNewHeader
          onBack={handleBack}
          openGenerateDialog={openGenerateDialog}
          setOpenGenerateDialog={setOpenGenerateDialog}
          openGraphJsonPromptDialog={openGraphJsonPromptDialog}
          setOpenGraphJsonPromptDialog={setOpenGraphJsonPromptDialog}
          openPasteQuestionDialog={openPasteQuestionDialog}
          setOpenPasteQuestionDialog={setOpenPasteQuestionDialog}
          examModules={examModules}
          modulesLoading={modulesLoading}
          formData={formData}
          handleApply={handleApply}
          handleGenerateClick={handleGenerateClick}
          onCheckWithRules={handleCheckWithRules}
          onCheckWithGpt={handleCheckWithGpt}
          onGenerateGraphJson={handleGenerateGraphJson}
          checkRulesLoading={checkRulesLoading}
          checkGptLoading={checkGptLoading}
          generateGraphLoading={generateGraphLoading}
          createLoading={createLoading}
        />

        <QuestionEditForm
          formData={formData}
          onFormDataChange={handleFormDataChange}
          question={null}
          examModules={examModules}
          modulesLoading={modulesLoading}
          isEditing={false}
        />

        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2}}>
          <Button onClick={handleBack} disabled={createLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAndAddAnother}
            variant="outlined"
            disabled={createLoading}
            sx={{
              borderColor: '#1B263B',
              color: '#1B263B',
              '&:hover': {borderColor: '#1B263B', bgcolor: '#1B263B', color: 'white'},
            }}
          >
            {createLoading ? <CircularProgress size={20} /> : 'Create and Add Another'}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createLoading}
            sx={{
              bgcolor: '#1B263B',
              '&:hover': {bgcolor: '#1B263B', opacity: 0.8},
            }}
          >
            {createLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Question'}
          </Button>
        </Box>
      </Stack>

      <StatusNoteDialog
        open={checkResultDialogOpen}
        onClose={handleCloseCheckResultDialog}
        statusNote={checkResult?.statusNote}
        title="Question Check Result"
        headerContent={
          checkResult?.status && (
            <Alert
              severity={
                checkResult.status === QuestionStatus.CHECKED || checkResult.status === QuestionStatus.VALIDATED
                  ? 'success'
                  : checkResult.status === QuestionStatus.FLAGGED
                    ? 'error'
                    : 'info'
              }
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Status: {checkResult.status}
              </Typography>
            </Alert>
          )
        }
      />
    </ContentContainer>
  );
}
